import React, { FC, useCallback, useState, useEffect } from "react";
import { Dialog, DialogContent, DialogActions, Button, Checkbox } from "@xapp/components";
import { SummarySubscription, SummaryFrequency, NotificationChannel, ReportGrouping } from "generated/graphql";
import { SubscribableApp } from "../types";

export interface SummarySubscriptionEditorProps {
    subscription?: SummarySubscription | null;
    subscribableApps: SubscribableApp[];
    active: boolean;
    onSave: (subscription: Partial<SummarySubscription>) => void;
    onDismiss: () => void;
}

export const SummarySubscriptionEditor: FC<SummarySubscriptionEditorProps> = (props) => {
    const { subscription, subscribableApps, active, onSave, onDismiss } = props;

    const [selectedAppIds, setSelectedAppIds] = useState<string[]>([]);
    const [frequency, setFrequency] = useState<SummaryFrequency>(SummaryFrequency.Weekly);
    const [grouping, setGrouping] = useState<ReportGrouping>(ReportGrouping.Combined);
    const [channel] = useState<NotificationChannel>(NotificationChannel.Email);

    useEffect(() => {
        if (active && subscription) {
            setSelectedAppIds(subscription.appIds);
            setFrequency(subscription.frequency);
            setGrouping(subscription.grouping);
        } else if (active && !subscription) {
            setSelectedAppIds([]);
            setFrequency(SummaryFrequency.Weekly);
            setGrouping(ReportGrouping.Combined);
        }
    }, [subscription, active]);

    const handleSave = useCallback(() => {
        const savedSubscription: Partial<SummarySubscription> = {
            appIds: selectedAppIds,
            frequency,
            grouping,
            channel,
        };

        onSave(savedSubscription);
    }, [selectedAppIds, frequency, grouping, channel, onSave]);

    const isValid = selectedAppIds.length > 0;

    return (
        <Dialog
            active={active}
            onDismiss={onDismiss}
            title={subscription ? "Edit Summary Subscription" : "Add Summary Subscription"}
        >
            <DialogContent>
                <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                    <div>
                        <label>Select Apps</label>
                        <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginTop: "8px" }}>
                            {subscribableApps.map((app) => (
                                <Checkbox
                                    key={app.appId}
                                    checked={selectedAppIds.includes(app.appId)}
                                    label={app.name}
                                    onChange={(checked) => {
                                        if (checked) {
                                            setSelectedAppIds([...selectedAppIds, app.appId]);
                                        } else {
                                            setSelectedAppIds(selectedAppIds.filter((id) => id !== app.appId));
                                        }
                                    }}
                                />
                            ))}
                        </div>
                    </div>

                    <div>
                        <label>Frequency</label>
                        <div style={{ display: "flex", gap: "16px", marginTop: "8px" }}>
                            <Checkbox
                                checked={frequency === SummaryFrequency.Weekly}
                                label="Weekly"
                                onChange={() => setFrequency(SummaryFrequency.Weekly)}
                            />
                            <Checkbox
                                checked={frequency === SummaryFrequency.Monthly}
                                label="Monthly"
                                onChange={() => setFrequency(SummaryFrequency.Monthly)}
                            />
                        </div>
                    </div>

                    <div>
                        <label>Grouping</label>
                        <div style={{ display: "flex", gap: "16px", marginTop: "8px" }}>
                            <Checkbox
                                checked={grouping === ReportGrouping.Individual}
                                label="One email per app"
                                onChange={() => setGrouping(ReportGrouping.Individual)}
                            />
                            <Checkbox
                                checked={grouping === ReportGrouping.Combined}
                                label="One combined email"
                                onChange={() => setGrouping(ReportGrouping.Combined)}
                            />
                        </div>
                    </div>

                    <div>
                        <p><strong>Channel:</strong> Email (SMS coming soon)</p>
                    </div>
                </div>
            </DialogContent>
            <DialogActions>
                <Button onClick={onDismiss}>Cancel</Button>
                <Button variation="primary" onClick={handleSave} disabled={!isValid}>
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};
